import Link from 'next/link';
import {
  ArrowUpRight,
  Building2,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  Phone,
  Store,
  Youtube,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import Tooltip from './Tooltip';

export default function SiteFooter() {
  const { t } = useI18n();
  const currentYear = new Date().getFullYear();
  const chipLabelClass =
    'inline-flex items-center rounded-full border border-white/30 bg-white/10 px-3 py-1 text-[10px] font-[700] uppercase tracking-[0.24em] text-white';

  const socials = [
    { icon: Facebook, href: 'https://www.facebook.com/etctunisia/', label: t('footer.facebook') },
    { icon: Instagram, href: 'https://www.instagram.com/essid_trading_company/', label: t('footer.instagram') },
  ];

  return (
    <footer className="relative overflow-hidden bg-[var(--dash-main)] pb-12 pt-24 m-0 md:m-16 rounded-none md:rounded-3xl">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-0 right-0 top-0 h-full bg-gradient-to-br from-[var(--dash-black)] to-transparent mix-blend-color-burn opacity-20 z-[3]" />
        <div className="absolute inset-0 z-0 bg-[url('/logos/letter.svg')] bg-no-repeat mix-blend-lighten bg-[position:100%_80%] md:bg-[position:100%_100%] bg-[size:160%] md:bg-[size:53%] opacity-10" />
        <div className="absolute left-0 right-0 bottom-0 h-[30%] bg-gradient-to-t from-[var(--dash-main)] to-transparent z-[1]" />
        <div className="absolute left-0 right-0 top-0 h-[30%] bg-gradient-to-b from-[var(--dash-main)] to-transparent z-[2]" />
      </div>

      <div className="container relative z-10 mx-auto px-6 lg:px-16">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="flex flex-col items-start lg:col-span-4">
            <div className="mb-8">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/logos/logo-white.svg"
                alt={t('footer.logoAlt')}
                width="468"
                height="212"
                className="w-48"
              />
            </div>
            <p className="mb-8 max-w-sm text-md font-medium leading-relaxed text-white">
              {t('footer.brandText')}
            </p>

            <div className="mb-0 md:mb-10">
              <div className="flex items-center gap-3">
                {socials.map((social) => (
                  <Tooltip key={social.label} content={social.label}>
                    <motion.a
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,1)', color: '#ff5d10' }}
                      className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-all"
                      aria-label={social.label}
                    >
                      <social.icon size={20} strokeWidth={2} />
                    </motion.a>
                  </Tooltip>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:col-span-6">
            <div className="space-y-12">
              <section>
                <div className="mb-4 flex items-center gap-3">
                  <Phone size={14} className="text-white" />
                  <span className={chipLabelClass}>
                    {t('footer.phone')}
                  </span>
                </div>
                <div className="space-y-1 text-lg font-[600] tracking-tight text-white">
                  <a href="tel:+21626363025" className="block transition-transform hover:translate-x-1">
                    +216 26 363 025
                  </a>
                  <a href="tel:+21672363000" className="block transition-transform hover:translate-x-1">
                    +216 72 363 000
                  </a>
                </div>
              </section>

              <section>
                <div className="mb-4 flex items-center gap-3">
                  <Mail size={14} className="text-white" />
                  <span className={chipLabelClass}>
                    {t('footer.email')}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <a
                    href="mailto:sales@etc.tn"
                    className="block w-fit border-b-2 border-white/30 text-lg font-medium text-white transition-all hover:border-white"
                  >
                    sales@etc.tn
                  </a>
                </div>
              </section>
            </div>

            <div className="space-y-12">
              <section>
                <div className="mb-4 flex items-center gap-3">
                  <Building2 size={14} className="text-white" />
                  <span className={chipLabelClass}>
                    {t('footer.headquarters')}
                  </span>
                </div>
                <p className="text-md font-medium leading-relaxed text-white/90">
                  {t('footer.headquartersAddress')}
                </p>
              </section>

              <section>
                <div className="mb-4 flex items-center gap-3">
                  <Store size={14} className="text-white" />
                  <span className={chipLabelClass}>
                    {t('footer.showroom')}
                  </span>
                </div>
                <p className="text-md font-medium leading-relaxed text-white/90">
                  {t('footer.showroomAddress')}
                </p>
              </section>
            </div>

          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-8 pt-12 md:flex-row">
          <div className="flex flex-col items-center gap-6 text-[10px] font-[600] uppercase tracking-[0.3em] text-white/60 md:flex-row md:gap-12">
            <p>
              &copy; {currentYear} {t('footer.rights')}
            </p>
            <div className="hidden gap-8 md:flex">
              <Link href="/privacy" className="transition-colors hover:text-white">
                {t('titlebar.routes.privacy')}
              </Link>
              <Link href="/terms" className="transition-colors hover:text-white">
                {t('titlebar.routes.terms')}
              </Link>
              <Link href="/legal" className="transition-colors hover:text-white">
                {t('titlebar.routes.legal')}
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-[9px] font-[600] uppercase tracking-widest text-white/80">
              {t('footer.developedBy')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
